import React from 'react';
import styles from './Reviews.module.scss';
import ReviewsHeader from './ReviewsHeader';
import ReviewsTable, { ReviewRow } from './ReviewsTable';
import Spinner from 'components/UI/V3/Spinner';
import SetupReviewsDrawer from './SetupReviewsDrawer';
import moment from 'moment-timezone';
import { formatRawDate } from 'utils/DateUtils';
import { DateRangeOption } from 'components/UI/V3/DateRangePickerDropdown';
import { SelectDropdownOption } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import { useDispatch, useSelector } from 'react-redux';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import { fetchReviewsThunk, ReviewsState } from '@spike/reviews-action';
import { store, RootState } from 'store';
import OrderDetail from 'components/OrderDetail';

const Reviews: React.FC = () => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper() as any;

    const reviewsSlice = useSelector<RootState, ReviewsState | undefined>(s => (s as any).reviews);
    const loading = reviewsSlice?.loading ?? false;
    const [searchText, setSearchText] = React.useState('');
    // Default: All time (null) and All ratings (all selected below)
    const [dateRange, setDateRange] = React.useState<DateRangeOption['value']>(null);
    type Rating = 1 | 2 | 3 | 4 | 5;
    const allRatings: Array<SelectDropdownOption<Rating>> = [
        { id: 1, label: '1 Star' },
        { id: 2, label: '2 Stars' },
        { id: 3, label: '3 Stars' },
        { id: 4, label: '4 Stars' },
        { id: 5, label: '5 Stars' }
    ];
    const [ratings, setRatings] = React.useState<Array<SelectDropdownOption<Rating>>>(allRatings);

    // Pagination state (local source of truth)
    const [page, setPage] = React.useState(1);
    const [size, setSize] = React.useState(10);
    const [showSetup, setShowSetup] = React.useState(false);
    // Booking / Order detail drawer state (mirrors pattern from PromoCodesUsage)
    const [showOrderDetails, setShowOrderDetails] = React.useState<{
        bookingId: number;
        orderId: number;
    } | null>(null);

    // Trigger backend fetch when filters/pagination change
    React.useEffect(() => {
        const query = searchText.trim() || undefined;
        const ratingIds = ratings.length === 5 ? undefined : ratings.map(r => r.id);
        const from = dateRange?.from ? dateRange.from.clone().startOf('day').format('YYYY-MM-DD') : undefined;
        const to = dateRange?.to ? dateRange.to.clone().endOf('day').format('YYYY-MM-DD') : undefined;

        // If filters (query/ratings/from/to) changed, reset list and force page=1
        const filterSignature = [query || '', (ratingIds || []).join(','), from || '', to || ''].join('|');
        const lastFilterRef = (Reviews as any)._lastFilterRef || ((Reviews as any)._lastFilterRef = { current: '' });
        if (lastFilterRef.current !== filterSignature) {
            lastFilterRef.current = filterSignature;
            // reset slice to avoid concatenation of previous results
            (dispatch as any)({ type: 'reviews/reset' });
            if (page !== 1) {
                // Sync page, the effect will re-run with page=1
                setPage(1);
                return;
            }
        }
        try {
            (dispatch as any)(fetchReviewsThunk(apiClientWrapper, query, ratingIds as any, from, to, page, size));
        } catch (e) {
            // swallow; reducer will handle error/notification
        }
        // Only re-run when stable primitives change
    }, [
        dispatch,
        searchText,
        page,
        size,
        ratings.map(r => r.id).join(','),
        dateRange?.from?.valueOf(),
        dateRange?.to?.valueOf()
    ]);

    const timeZone = store.getState().login.auth.timeZone!;

    // Derive rows and metrics from Redux slice
    const reviews = reviewsSlice?.reviews ?? [];

    const pagination = reviewsSlice?.pagination;
    const totalPages =
        pagination?.totalPages ?? Math.max(1, Math.ceil((pagination?.totalRecords ?? reviews.length) / size));

    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    const currentPageReviews = reviews.slice(startIndex, endIndex);

    // Helpers to safely pick names from various possible shapes/keys
    const pickValue = (...vals: Array<any>) => {
        for (const v of vals) {
            if (v === undefined || v === null) continue;
            if (typeof v === 'string') {
                if (v.trim().length > 0) return v.trim();
            } else {
                return v;
            }
        }
        return undefined;
    };

    const scanByKeyPattern = (obj: any, patterns: RegExp[]): string | undefined => {
        if (!obj || typeof obj !== 'object') return undefined;
        const entries = Object.entries(obj) as Array<[string, any]>;
        for (const [k, v] of entries) {
            if (typeof v === 'string' && patterns.some(p => p.test(k))) {
                const trimmed = v.trim();
                if (trimmed) return trimmed;
            }
        }
        return undefined;
    };

    const deepScanByKeyPattern = (obj: any, patterns: RegExp[], maxDepth = 3): string | undefined => {
        const visited = new Set<any>();
        const scan = (o: any, depth: number): string | undefined => {
            if (!o || typeof o !== 'object') return undefined;
            if (visited.has(o) || depth > maxDepth) return undefined;
            visited.add(o);
            for (const [k, v] of Object.entries(o)) {
                if (typeof v === 'string' && patterns.some(p => p.test(k))) {
                    const t = v.trim();
                    if (t) return t;
                }
                if (v && typeof v === 'object') {
                    const found = scan(v, depth + 1);
                    if (found) return found;
                }
            }
            return undefined;
        };
        return scan(obj, 0);
    };

    const getClientName = (r: any): string =>
        pickValue(
            r.clientName,
            r.client_name,
            r.customerName,
            r.customer_name,
            r.client_full_name,
            r.customer_full_name,
            r.client?.name,
            r.client?.fullName,
            r.customer?.name,
            r.customer?.fullName,
            scanByKeyPattern(r, [/^client.*name$/i, /^customer.*name$/i]),
            deepScanByKeyPattern(r, [/^client.*name$/i, /^customer.*name$/i])
        ) || '—';

    const getPetName = (r: any): string =>
        pickValue(
            r.petName,
            r.pet_name,
            r.pet_full_name,
            r.pet?.name,
            r.pet?.fullName,
            r.pet?.display_name,
            scanByKeyPattern(r, [/^pet.*name$/i]),
            deepScanByKeyPattern(r, [/^pet.*name$/i])
        ) || '—';

    const getStaffName = (r: any): string =>
        pickValue(
            r.staffName,
            r.staff_name,
            r.staff_full_name,
            r.staff?.name,
            r.staff?.fullName,
            r.employee?.name,
            scanByKeyPattern(r, [/^staff.*name$/i, /^employee.*name$/i]),
            deepScanByKeyPattern(r, [/^staff.*name$/i, /^employee.*name$/i, /^groomer.*name$/i, /^provider.*name$/i])
        ) || '—';

    const rows: ReviewRow[] = currentPageReviews.map(r => {
        const bookingId =
            (r as any).bookingId || (r as any).booking_id || (r as any).booking?.id || (r as any).order?.bookingId;
        const orderId = (r as any).orderId || (r as any).order_id || (r as any).order?.id;
        return {
            id: String((r as any).id),
            bookingId: Number(bookingId) || 0,
            orderId: Number(orderId) || 0,
            client: getClientName(r),
            pet: getPetName(r),
            date: (r as any).createdAt
                ? formatRawDate((r as any).createdAt)
                : (r as any).created_at
                ? moment((r as any).created_at)
                      .tz(timeZone!)
                      .format('MMM D, YYYY')
                : '',
            staff: getStaffName(r),
            rating: Number((r as any).rating ?? (r as any).stars ?? 0),
            review: (r as any).comment ?? (r as any).review ?? '',
            tags: (r as any).tags ?? (r as any).tag_list ?? []
        };
    });

    const totalReviews = pagination?.totalRecords ?? reviews.length;
    const positiveCount = reviews.filter(r => (r.rating ?? 0) >= 4).length;
    const negativeCount = reviews.filter(r => (r.rating ?? 0) <= 3).length;
    const avgRating =
        reviews.length > 0 ? reviews.reduce((sum, r) => sum + Number(r.rating ?? 0), 0) / reviews.length : 0;

    return (
        <div className={styles.container}>
            <ReviewsHeader
                searchText={searchText}
                onChangeSearchText={v => {
                    setSearchText(v);
                    setPage(1);
                }}
                dateRange={dateRange}
                onChangeDateRange={dr => {
                    setDateRange(dr);
                    setPage(1);
                }}
                timeZone={timeZone}
                ratings={ratings}
                onChangeRatings={vals => {
                    setRatings(vals);
                    setPage(1);
                }}
                avgRating={avgRating}
                totalReviews={totalReviews}
                positiveCount={positiveCount}
                negativeCount={negativeCount}
                onSetupReviews={() => setShowSetup(true)}
            />

            {loading ? (
                <div className={styles.spinnerContainer}>
                    <Spinner />
                </div>
            ) : (
                <ReviewsTable
                    rows={rows}
                    className={styles.table}
                    pagination={{ page, totalPages, size }}
                    onPageChange={setPage}
                    onSizeChange={newSize => {
                        setSize(newSize);
                        setPage(1);
                    }}
                    onViewBooking={(bookingId, orderId) => {
                        if (!bookingId) return;
                        // Align behavior with PromoCodesUsage: store ids and render <OrderDetail /> conditionally
                        setShowOrderDetails({ bookingId, orderId });
                    }}
                />
            )}

            {showSetup && (
                <SetupReviewsDrawer
                    open={showSetup}
                    onClose={() => setShowSetup(false)}
                />
            )}

            {showOrderDetails && (
                <OrderDetail
                    bookingId={showOrderDetails.bookingId}
                    orderId={showOrderDetails.orderId}
                    onClose={() => setShowOrderDetails(null)}
                    // If OrderDetail supports onCheckout or other props later, add them here for parity.
                />
            )}
        </div>
    );
};

export default Reviews;
